import { useState } from 'react'
import { colors, formatNumber } from '../styles/tokens'

// =======================================================================
// FirstPrinciplesGuide — a compact, collapsible educational strip shown
// inline within the estimating workbench (never a separate page).
// Pure UI/education layer: no calculation logic lives here. The numeric
// example below is a fixed illustration only, not derived from any live
// data or backend call.
// =======================================================================

const STORAGE_KEY = 'fp_guide_seen_v1'

const STEPS = [
  { icon: '👷', title: 'Resources', text: 'The people, equipment, and other cost resources used to perform the work.' },
  { icon: '💰', title: 'Resource Rates', text: 'The cost of each resource, based on the selected Pricing Date.' },
  { icon: '👥', title: 'Crew', text: 'The combination of resources working together as one production team.' },
  { icon: '📈', title: 'Productivity', text: 'How much work the crew can produce in one shift, or how many man-hours are required for one unit.' },
  { icon: '⚙️', title: 'Productivity Factors', text: 'Adjustments for site conditions such as working at height, restricted access, complexity, or other productivity impacts.' },
  { icon: '🧮', title: 'First-Principles Rate', text: 'The system combines crew cost and productivity to derive the cost of one BOQ unit.' },
  { icon: '📋', title: 'BOQ Cost', text: 'The derived unit rate is multiplied by the BOQ quantity to produce the line cost.' },
]

export default function FirstPrinciplesGuide() {
  const [expanded, setExpanded] = useState(() => {
    try { return !localStorage.getItem(STORAGE_KEY) } catch { return true }
  })

  function toggle() {
    setExpanded((v) => {
      const next = !v
      try { if (!next) localStorage.setItem(STORAGE_KEY, '1') } catch { /* ignore */ }
      return next
    })
  }

  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 14, overflow: 'hidden', background: '#FAFBFC' }}>
      <button
        onClick={toggle}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          ℹ How First-Principles Estimating Works
        </span>
        <span style={{ fontSize: 12, color: colors.textMuted }}>{expanded ? '▾ Hide' : '▸ Show'}</span>
      </button>

      {expanded && (
        <div style={{ padding: '4px 14px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto', gap: 0, marginBottom: 12 }}>
            {STEPS.map((step, i) => (
              <div key={step.title} style={{ display: 'flex', alignItems: 'stretch' }}>
                <div style={{ minWidth: 130, maxWidth: 150, padding: '8px 10px', background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6 }}>
                  <div style={{ fontSize: 16, marginBottom: 2 }}>{step.icon}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: colors.primary, marginBottom: 3 }}>{step.title}</div>
                  <div style={{ fontSize: 10.5, color: colors.textSecondary, lineHeight: 1.4 }}>{step.text}</div>
                </div>
                {i < STEPS.length - 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 6px', color: colors.textMuted, fontSize: 14 }}>→</div>
                )}
              </div>
            ))}
          </div>

          <div style={{ padding: '10px 12px', background: '#fff', border: `1px dashed ${colors.border}`, borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 6 }}>Example</div>
            <div style={{ fontSize: 12, color: colors.textPrimary, lineHeight: 1.9, fontFamily: 'monospace' }}>
              Crew Cost = 1,200 SAR/shift<br />
              Productivity = 10 m²/shift<br />
              Unit Rate = 1,200 ÷ 10 = <strong style={{ color: colors.primary }}>120 SAR/m²</strong><br />
              BOQ Quantity = 50 m²<br />
              Line Cost = 120 × 50 = <strong style={{ color: colors.primary }}>6,000 SAR</strong>
            </div>
            <div style={{ fontSize: 10.5, color: colors.textMuted, marginTop: 8, fontStyle: 'italic' }}>
              The calculation is performed by the estimation engine. These values are shown to explain how the rate is built.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
